import React from "react";
import { Link } from "react-router-dom";
import StaticPage from "./StaticPage";
import { ClipboardList, ArrowRight, School, Users, Target } from "lucide-react";

const SCHOOL_PAIN = [
  "Vendor follow-ups",
  "Quotation collection",
  "Order tracking",
  "Delivery coordination",
  "Record keeping",
  "Payment tracking",
];

const PARENT_PAIN = [
  "Long queues during book distribution",
  "Multiple visits for purchases",
  "Confusing order processes",
  "Difficulty with returns and exchanges",
  "Lack of order visibility",
];

export default function About() {
  return (
    <StaticPage
      title="About SchoolLane"
      subtitle="Building a modern procurement ecosystem for Indian schools."
    >
      <p>
        Schools spend countless hours coordinating with multiple suppliers for
        books, uniforms, stationery, library books, laboratory supplies and
        other educational requirements.
      </p>

      <h2>The problem schools face</h2>
      <p>Administrative teams often struggle with:</p>
      <ul className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 mb-6">
        {SCHOOL_PAIN.map((p) => (
          <li key={p}
              data-testid={`about-school-pain-${p.toLowerCase().replace(/\s+/g, '-')}`}
              className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
            <ClipboardList size={16} className="text-blue-700 mt-0.5 shrink-0" /> {p}
          </li>
        ))}
      </ul>

      <h2>The problem parents face</h2>
      <p>Parents face a different set of challenges:</p>
      <ul className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 mb-6">
        {PARENT_PAIN.map((p) => (
          <li key={p}
              data-testid={`about-parent-pain-${p.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
            <Users size={16} className="text-blue-700 mt-0.5 shrink-0" /> {p}
          </li>
        ))}
      </ul>

      <h2>Why we built SchoolLane</h2>
      <p>
        SchoolLane was created to simplify this entire experience. Our platform
        brings schools, suppliers and parents onto a single digital platform
        where procurement becomes faster, more organised and more transparent.
      </p>
      <p>
        For schools, SchoolLane helps reduce administrative workload, improve
        procurement visibility and save valuable staff time.
      </p>
      <p>
        For parents, SchoolLane aims to provide a convenient, organised and
        hassle-free way to access school-related purchases.
      </p>

      <h2>Our mission</h2>
      <div className="not-prose rounded-2xl border border-blue-100 bg-blue-50 px-6 py-5 flex items-start gap-4 mt-4 mb-8">
        <Target className="text-blue-700 shrink-0 mt-1" size={22} />
        <div>
          <div className="font-heading font-semibold text-slate-900 text-lg">
            To make school procurement smarter, faster and more efficient for
            everyone involved.
          </div>
          <p className="mt-2 text-sm text-slate-700">
            SchoolLane is committed to building a modern procurement ecosystem
            for Indian schools.
          </p>
        </div>
      </div>

      <div className="not-prose mt-10 rounded-2xl bg-slate-900 text-white p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-300">
            Talk to us
          </div>
          <div className="mt-1 text-2xl font-heading font-semibold">
            See how SchoolLane fits your school.
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/contact" data-testid="about-cta-consult"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-white text-slate-900 font-semibold text-sm hover:bg-slate-100 transition-colors duration-200">
            Request Procurement Consultation <ArrowRight size={16} />
          </Link>
          <Link to="/contact" data-testid="about-cta-demo"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-white/30 text-white font-semibold text-sm hover:bg-white/10 transition-colors duration-200">
            <School size={16} /> Book a Demo
          </Link>
        </div>
      </div>
    </StaticPage>
  );
}
